import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { AdminPanel } from '@/components/AdminPanel';
import { PersonalEventHostControls } from '@/components/PersonalEventHostControls';
import { SponsorBanner } from '@/components/SponsorBanner';
import { 
  ArrowLeft, Trophy, Users, Plus, Minus, Check,
  Medal, Target, User, Crown, Lock, Wifi, WifiOff,
  Send, CheckCircle2, UserPlus, Users2, UserCheck, X,
  Eye, Copy
} from 'lucide-react';
import { Input } from '@/components/ui/input';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^https/, 'wss').replace(/^http/, 'ws');
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

export const TournamentPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { member } = useAuth();
  
  const [tournament, setTournament] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [hasJoined, setHasJoined] = useState(false);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [submittingHole, setSubmittingHole] = useState(null);
  const [submittedHoles, setSubmittedHoles] = useState(new Set());
  const [wsConnected, setWsConnected] = useState(false);
  
  // Team state
  const [myTeam, setMyTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamLeaderboard, setTeamLeaderboard] = useState([]);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [partnerNumber, setPartnerNumber] = useState('');
  const [teamName, setTeamName] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttempts = useRef(0);

  // WebSocket connection with auto-reconnect
  useEffect(() => {
    if (!id) return;

    const connectWs = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return;
      
      const socket = new WebSocket(`${WS_BASE}/api/tournaments/${id}/ws`);
      wsRef.current = socket;

      socket.onopen = () => {
        setWsConnected(true);
        reconnectAttempts.current = 0;
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'leaderboard_update') {
            if (data.is_team) {
              setTeamLeaderboard(data.leaderboard);
            } else {
              setLeaderboard(data.leaderboard);
            }
          }
        } catch (e) {
          // ignore non-JSON messages
        }
      };

      socket.onclose = () => {
        setWsConnected(false);
        wsRef.current = null;
        // Reconnect with exponential backoff (max 30s)
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        reconnectAttempts.current += 1;
        reconnectTimerRef.current = setTimeout(connectWs, delay);
      };

      socket.onerror = () => {
        socket.close();
      };
    };

    connectWs();

    // Ping every 25s to keep connection alive
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 25000);

    return () => {
      clearInterval(pingInterval);
      clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on intentional close
        wsRef.current.close();
      }
    };
  }, [id]);

  const fetchTournament = useCallback(async () => {
    try {
      const [tournamentRes, leaderboardRes, participantsRes] = await Promise.all([
        axios.get(`${API_URL}/tournaments/${id}`),
        axios.get(`${API_URL}/tournaments/${id}/leaderboard`),
        axios.get(`${API_URL}/tournaments/${id}/participants`)
      ]);
      
      setTournament(tournamentRes.data);
      setLeaderboard(leaderboardRes.data);
      setParticipants(leaderboardRes.data);
      
      const isTeamTournament = !!tournamentRes.data.team_format;
      
      // Fetch team data for team tournaments
      if (isTeamTournament) {
        const [teamsRes, teamLbRes] = await Promise.all([
          axios.get(`${API_URL}/tournaments/${id}/teams`),
          axios.get(`${API_URL}/tournaments/${id}/team-leaderboard`)
        ]);
        setTeams(teamsRes.data);
        setTeamLeaderboard(teamLbRes.data);
        
        // Find my team
        const mine = teamsRes.data.find(t => 
          (t.captain_id === member?.id || t.partner_id === member?.id) && t.status === 'active'
        );
        setMyTeam(mine || null);
        
        // Check for pending invites for me
        const myPending = teamsRes.data.filter(t => t.partner_id === member?.id && t.status === 'pending');
        setPendingInvites(myPending);
      }
      
      const isJoined = participantsRes.data.some(p => p.member_id === member?.id);
      setHasJoined(isJoined);
      
      if (isJoined) {
        const memberScore = leaderboardRes.data.find(e => e.member_id === member?.id);
        if (memberScore?.hole_scores?.length > 0) {
          const scoreMap = {};
          const submitted = new Set();
          memberScore.hole_scores.forEach(hs => {
            scoreMap[hs.hole_number] = hs.strokes;
            submitted.add(hs.hole_number);
          });
          // Fill remaining holes with par defaults
          tournamentRes.data.holes.forEach(h => {
            if (!(h.hole_number in scoreMap)) {
              scoreMap[h.hole_number] = h.par;
            }
          });
          setScores(scoreMap);
          setSubmittedHoles(submitted);
        } else {
          const initialScores = {};
          tournamentRes.data.holes.forEach(h => {
            initialScores[h.hole_number] = h.par;
          });
          setScores(initialScores);
          setSubmittedHoles(new Set());
        }
      }
    } catch (error) {
      toast.error('Failed to load tournament');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, member?.id, navigate]);

  useEffect(() => {
    fetchTournament();
  }, [fetchTournament]);

  const handleJoin = async () => {
    try {
      await axios.post(`${API_URL}/tournaments/${id}/join`, {
        member_id: member.id
      });
      toast.success('Successfully joined tournament!');
      setHasJoined(true);
      const initialScores = {};
      tournament.holes.forEach(h => {
        initialScores[h.hole_number] = h.par;
      });
      setScores(initialScores);
      setSubmittedHoles(new Set());
      fetchTournament();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join tournament');
    }
  };

  const [payingEntry, setPayingEntry] = useState(false);

  const handlePayEntry = async () => {
    if (!member?.id) return;
    setPayingEntry(true);
    try {
      const res = await axios.post(`${API_URL}/payments/tournament-entry/create-checkout`, {
        member_id: member.id,
        tournament_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start checkout');
      setPayingEntry(false);
    }
  };

  // Handle return from Stripe entry-fee checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('entry_session_id');
    const cancelled = params.get('entry_cancelled');
    if (cancelled) {
      toast.info('Entry payment cancelled');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    if (sid && member?.id) {
      (async () => {
        try {
          const res = await axios.get(`${API_URL}/payments/tournament-entry/session-status/${sid}`);
          if (res.data.status === 'success') {
            toast.success('Payment confirmed — you\'re in! 30-day Pro trial activated.');
            fetchTournament();
          } else {
            toast.info('Payment still processing. Refresh in a moment.');
          }
        } catch (err) {
          toast.error('Could not verify payment');
        } finally {
          window.history.replaceState({}, '', window.location.pathname);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.id]);

  const updateScore = (holeNumber, delta) => {
    setScores(prev => ({
      ...prev,
      [holeNumber]: Math.max(1, (prev[holeNumber] || 0) + delta)
    }));
  };

  // Submit score for a single hole (auto-routes to team endpoint if team tournament)
  const submitHoleScore = async (holeNumber) => {
    setSubmittingHole(holeNumber);
    try {
      if (isTeamTournament && myTeam) {
        await submitTeamHoleScore(holeNumber);
      } else {
        await axios.post(`${API_URL}/tournaments/${id}/scores/hole`, {
          member_id: member.id,
          hole_number: holeNumber,
          strokes: scores[holeNumber]
        });
        setSubmittedHoles(prev => new Set([...prev, holeNumber]));
        toast.success(`Hole ${holeNumber} saved`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to save hole ${holeNumber}`);
    } finally {
      setSubmittingHole(null);
    }
  };

  // Submit all remaining holes at once
  const submitAllRemaining = async () => {
    const unsubmitted = tournament.holes.filter(h => !submittedHoles.has(h.hole_number));
    if (unsubmitted.length === 0) {
      toast.info('All holes already submitted');
      return;
    }
    for (const hole of unsubmitted) {
      await submitHoleScore(hole.hole_number);
    }
  };

  // Team functions
  const isTeamTournament = tournament?.team_format != null;

  const handleCreateTeam = async () => {
    if (!partnerNumber.trim()) { toast.error('Enter partner member number'); return; }
    setCreatingTeam(true);
    try {
      await axios.post(`${API_URL}/tournaments/${id}/teams`, {
        captain_id: member.id,
        partner_member_number: partnerNumber.trim(),
        team_name: teamName.trim() || null
      });
      toast.success('Team invite sent!');
      setShowCreateTeam(false);
      setPartnerNumber('');
      setTeamName('');
      fetchTournament();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleRespondInvite = async (teamId, accept) => {
    try {
      await axios.put(`${API_URL}/teams/${teamId}/respond`, {
        member_id: member.id,
        accept
      });
      toast.success(accept ? 'Team joined!' : 'Invite declined');
      fetchTournament();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    }
  };

  const submitTeamHoleScore = async (holeNumber) => {
    if (!myTeam) return;
    setSubmittingHole(holeNumber);
    try {
      await axios.post(`${API_URL}/tournaments/${id}/teams/${myTeam.id}/scores/hole`, {
        team_id: myTeam.id,
        member_id: member.id,
        hole_number: holeNumber,
        strokes: scores[holeNumber]
      });
      setSubmittedHoles(prev => new Set([...prev, holeNumber]));
      toast.success(`Hole ${holeNumber} saved`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to save hole ${holeNumber}`);
    } finally {
      setSubmittingHole(null);
    }
  };

  const getTeamFormatLabel = (format) => {
    if (format === 'best_ball') return 'Best Ball';
    if (format === 'combined') return 'Combined';
    if (format === 'alternate_shot') return 'Alternate Shot';
    return format;
  };


  const getScoreClass = (strokes, par) => {
    const diff = strokes - par;
    if (diff <= -2) return 'score-eagle';
    if (diff === -1) return 'score-birdie';
    if (diff === 0) return 'score-par';
    if (diff === 1) return 'score-bogey';
    return 'score-double-bogey';
  };

  const getScoreName = (strokes, par) => {
    const diff = strokes - par;
    if (diff <= -3) return 'Albatross';
    if (diff === -2) return 'Eagle';
    if (diff === -1) return 'Birdie';
    if (diff === 0) return 'Par';
    if (diff === 1) return 'Bogey';
    if (diff === 2) return 'Dbl Bogey';
    if (diff === 3) return 'Trpl Bogey';
    return `+${diff}`;
  };

  const getMyTotalStrokes = () => {
    if (!tournament) return 0;
    let total = 0;
    submittedHoles.forEach(h => { total += scores[h] || 0; });
    return total;
  };

  const getMyScoreToPar = () => {
    if (!tournament) return 0;
    let strokes = 0;
    let par = 0;
    submittedHoles.forEach(holeNum => {
      strokes += scores[holeNum] || 0;
      const holeConfig = tournament.holes.find(h => h.hole_number === holeNum);
      if (holeConfig) par += holeConfig.par;
    });
    return strokes - par;
  };

  const formatScoreToPar = (score) => {
    if (score === 0) return 'E';
    if (score > 0) return `+${score}`;
    return score.toString();
  };

  const canJoinTournament = () => {
    if (!tournament) return false;
    if (tournament.tournament_type === 'pro') {
      return member?.membership_tier === 'pro';
    }
    return true;
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <span className="rank-gold px-2 py-1 rounded text-xs font-bold">1st</span>;
    if (rank === 2) return <span className="rank-silver px-2 py-1 rounded text-xs font-bold">2nd</span>;
    if (rank === 3) return <span className="rank-bronze px-2 py-1 rounded text-xs font-bold">3rd</span>;
    return <span className="text-muted-foreground font-mono">{rank}</span>;
  };

  const getThruLabel = (holesCompleted, totalHoles) => {
    if (holesCompleted === 0) return '-';
    if (holesCompleted >= totalHoles) return 'F';
    return holesCompleted.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl font-heading">Loading...</div>
      </div>
    );
  }

  if (!tournament) return null;

  const allHolesSubmitted = submittedHoles.size >= tournament.num_holes;

  return (
    <div className="min-h-screen bg-[#050505]">
      <SponsorBanner variant="top-banner" />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-muted-foreground hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <img 
            src={TGA_LOGO} 
            alt="TGA Pro Tour" 
            className="h-10 object-contain cursor-pointer"
            onClick={() => navigate('/dashboard')}
          />
          <Button 
            variant="ghost"
            onClick={() => navigate('/profile')}
            className="text-muted-foreground hover:text-white"
            data-testid="nav-profile-btn"
          >
            <User className="mr-2 h-4 w-4" />
            Profile
          </Button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Tournament Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h1 className="text-3xl sm:text-4xl font-heading text-white">
                  {tournament.name}
                </h1>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  tournament.status === 'open' ? 'status-open' :
                  tournament.status === 'in_progress' ? 'status-in-progress' :
                  tournament.status === 'closed' ? 'bg-yellow-500/20 text-yellow-500 border border-yellow-500' :
                  tournament.status === 'archived' ? 'bg-gray-500/20 text-gray-500 border border-gray-500' :
                  'status-completed'
                }`}>
                  {tournament.status === 'open' ? 'Open' :
                   tournament.status === 'in_progress' ? 'In Progress' :
                   tournament.status === 'closed' ? 'Closed' :
                   tournament.status === 'archived' ? 'Archived' :
                   'Completed'}
                </span>
                {tournament.tournament_type === 'pro' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border border-amber-500/50">
                    <Crown className="w-3 h-3" />
                    Pro Tour
                  </span>
                )}
                {isTeamTournament && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-secondary/15 text-secondary border border-secondary/30" data-testid="team-format-badge">
                    <Users2 className="w-3 h-3" />
                    {getTeamFormatLabel(tournament.team_format)}
                  </span>
                )}
              </div>
              {tournament.description && (
                <p className="text-muted-foreground mb-3">{tournament.description}</p>
              )}
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Target className="w-4 h-4" />
                  {tournament.num_holes} Holes
                </span>
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Users className="w-4 h-4" />
                  {tournament.participants_count} Players
                </span>
                <span className="flex items-center gap-1 text-primary font-mono font-bold">
                  Par {tournament.total_par}
                </span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/spectate/${tournament.id}`);
                    toast.success('Spectator link copied!');
                  }}
                  className="flex items-center gap-1 text-muted-foreground hover:text-white transition-colors text-sm"
                  data-testid="share-spectator-btn"
                >
                  <Eye className="w-4 h-4" />
                  Share
                </button>
              </div>
            </div>
            
            {!hasJoined && !myTeam && tournament.status === 'open' && (
              <div className="flex flex-col items-end gap-2">
                {isTeamTournament ? (
                  <>
                    {pendingInvites.length > 0 ? (
                      <div className="space-y-2">
                        {pendingInvites.map(inv => (
                          <div key={inv.id} className="flex items-center gap-2 p-3 bg-card border border-primary/30 rounded-lg" data-testid={`invite-${inv.id}`}>
                            <UserCheck className="w-4 h-4 text-primary shrink-0" />
                            <span className="text-sm text-white">{inv.captain_name} invited you</span>
                            <Button size="sm" onClick={() => handleRespondInvite(inv.id, true)} className="bg-secondary text-black h-7 px-3 text-xs" data-testid="accept-invite-btn">Accept</Button>
                            <Button size="sm" variant="ghost" onClick={() => handleRespondInvite(inv.id, false)} className="text-muted-foreground h-7 px-2 text-xs" data-testid="decline-invite-btn"><X className="w-3 h-3" /></Button>
                          </div>
                        ))}
                      </div>
                    ) : showCreateTeam ? (
                      <div className="p-4 bg-card border border-white/10 rounded-lg space-y-3 w-72" data-testid="create-team-form">
                        <Input
                          placeholder="Team name (optional)"
                          value={teamName}
                          onChange={(e) => setTeamName(e.target.value)}
                          className="bg-muted border-border text-sm"
                          data-testid="team-name-input"
                        />
                        <Input
                          placeholder="Partner member # (e.g. TGA-XXXXX)"
                          value={partnerNumber}
                          onChange={(e) => setPartnerNumber(e.target.value)}
                          className="bg-muted border-border text-sm"
                          data-testid="partner-number-input"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleCreateTeam} disabled={creatingTeam} className="bg-secondary text-black flex-1 text-xs" data-testid="send-invite-btn">
                            <Send className="w-3 h-3 mr-1" />{creatingTeam ? 'Sending...' : 'Send Invite'}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setShowCreateTeam(false)} className="text-xs">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        onClick={() => setShowCreateTeam(true)}
                        className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-pill px-6 btn-hover-scale glow-green"
                        data-testid="create-team-btn"
                      >
                        <Users2 className="mr-2 h-4 w-4" />
                        Create Team
                      </Button>
                    )}
                  </>
                ) : canJoinTournament() ? (
                  <Button 
                    onClick={handleJoin}
                    className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-pill px-6 btn-hover-scale glow-green"
                    data-testid="join-btn"
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Join Tournament
                  </Button>
                ) : (
                  <div className="text-right">
                    <Button 
                      disabled
                      className="bg-muted text-muted-foreground rounded-pill px-6 cursor-not-allowed"
                      data-testid="join-btn-disabled"
                    >
                      <Lock className="mr-2 h-4 w-4" />
                      Pro Members Only
                    </Button>
                    {tournament?.allow_paid_entry ? (
                      <div className="mt-2">
                        <Button
                          onClick={handlePayEntry}
                          disabled={payingEntry}
                          className="bg-amber-500 hover:bg-amber-400 text-black rounded-pill px-4 h-9"
                          data-testid="pay-entry-btn"
                        >
                          {payingEntry ? 'Redirecting…' : 'Pay $10 to Enter'}
                        </Button>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Includes a 30-day Pro trial
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-2">
                        <a href="/profile" className="text-amber-400 hover:underline">
                          Upgrade to Pro
                        </a>
                        {' '}to join this tournament
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Admin Panel */}
        {member?.is_admin && !tournament?.is_personal && (
          <AdminPanel 
            tournament={tournament} 
            member={member} 
            participants={participants}
            onUpdate={fetchTournament}
            onDelete={() => navigate('/dashboard')}
          />
        )}

        {/* Personal Event Host Panel */}
        {tournament?.is_personal && tournament?.host_id === member?.id && (
          <PersonalEventHostControls
            tournament={tournament}
            memberId={member.id}
            onUpdate={fetchTournament}
            onDelete={() => navigate('/personal-events')}
          />
        )}

        {/* Tabs */}
        <Tabs defaultValue={(hasJoined || myTeam) ? "scorecard" : "leaderboard"} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-muted mb-6">
            <TabsTrigger 
              value="scorecard"
              disabled={isTeamTournament ? !myTeam : !hasJoined}
              className="data-[state=active]:bg-primary data-[state=active]:text-white"
              data-testid="scorecard-tab"
            >
              Scorecard
            </TabsTrigger>
            <TabsTrigger 
              value="leaderboard"
              className="data-[state=active]:bg-primary data-[state=active]:text-white"
              data-testid="leaderboard-tab"
            >
              Leaderboard
            </TabsTrigger>
          </TabsList>

          {/* Scorecard Tab — Per-hole submission */}
          <TabsContent value="scorecard">
            {(isTeamTournament ? myTeam : hasJoined) ? (
              <div className="space-y-6">
                {/* Team Info Banner */}
                {isTeamTournament && myTeam && (
                  <Card className="bg-secondary/10 border-secondary/30" data-testid="team-info-banner">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Users2 className="w-5 h-5 text-secondary shrink-0" />
                        <div>
                          <p className="text-white font-medium">{myTeam.team_name}</p>
                          <p className="text-muted-foreground text-xs">
                            {myTeam.captain_name} & {myTeam.partner_name} — {getTeamFormatLabel(tournament.team_format)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Score Summary */}
                <Card className="bg-card border-white/10">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div>
                        <p className="text-muted-foreground text-sm">Your Score</p>
                        <div className="flex items-baseline gap-3">
                          <span className="text-4xl font-mono font-bold text-white" data-testid="my-total-strokes">
                            {getMyTotalStrokes() || '-'}
                          </span>
                          {submittedHoles.size > 0 && (
                            <span className={`text-2xl font-mono font-bold ${
                              getMyScoreToPar() < 0 ? 'text-secondary' :
                              getMyScoreToPar() === 0 ? 'text-primary' :
                              'text-destructive'
                            }`} data-testid="my-score-to-par">
                              ({formatScoreToPar(getMyScoreToPar())})
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1" data-testid="my-thru-indicator">
                          Thru {submittedHoles.size} of {tournament.num_holes}
                          {allHolesSubmitted && <span className="text-secondary ml-2 font-semibold">Complete</span>}
                        </p>
                      </div>
                      {!allHolesSubmitted && (
                        <Button
                          onClick={submitAllRemaining}
                          disabled={submittingHole !== null}
                          className="bg-primary hover:bg-primary/90 rounded-pill px-6 btn-hover-scale"
                          data-testid="submit-all-btn"
                        >
                          <Send className="mr-2 h-4 w-4" />
                          Submit All
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Per-Hole Scores */}
                <Card className="bg-card border-white/10">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">Scorecard</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Adjust your score and tap the check to submit each hole
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {tournament.holes.map((hole) => {
                        const strokes = scores[hole.hole_number] || hole.par;
                        const isSubmitted = submittedHoles.has(hole.hole_number);
                        const isSubmitting = submittingHole === hole.hole_number;
                        return (
                          <div 
                            key={hole.hole_number}
                            className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                              isSubmitted 
                                ? 'bg-secondary/10 border border-secondary/30' 
                                : 'bg-muted'
                            }`}
                            data-testid={`hole-score-${hole.hole_number}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {isSubmitted && <CheckCircle2 className="w-4 h-4 text-secondary shrink-0" />}
                                <span className="text-white font-heading text-lg">
                                  Hole {hole.hole_number}
                                </span>
                                <span className="text-muted-foreground text-sm">
                                  Par {hole.par}
                                </span>
                              </div>
                              <span className={`text-sm font-medium ${getScoreClass(strokes, hole.par)}`}>
                                {getScoreName(strokes, hole.par)}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => updateScore(hole.hole_number, -1)}
                                  disabled={strokes <= 1}
                                  className="score-btn score-btn-minus"
                                  data-testid={`hole-${hole.hole_number}-minus`}
                                >
                                  <Minus className="h-5 w-5" />
                                </button>
                                <span className="w-10 text-center text-2xl font-mono font-bold text-white">
                                  {strokes}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => updateScore(hole.hole_number, 1)}
                                  className="score-btn score-btn-plus"
                                  data-testid={`hole-${hole.hole_number}-plus`}
                                >
                                  <Plus className="h-5 w-5" />
                                </button>
                              </div>
                              <button
                                type="button"
                                onClick={() => submitHoleScore(hole.hole_number)}
                                disabled={isSubmitting}
                                className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                                  isSubmitted 
                                    ? 'bg-secondary/20 text-secondary hover:bg-secondary/30' 
                                    : 'bg-primary/20 text-primary hover:bg-primary/30'
                                } ${isSubmitting ? 'animate-pulse' : ''}`}
                                data-testid={`hole-${hole.hole_number}-submit`}
                                title={isSubmitted ? 'Update score' : 'Submit score'}
                              >
                                <Check className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="bg-card border-white/10">
                <CardContent className="p-12 text-center">
                  {canJoinTournament() ? (
                    <>
                      <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-xl font-heading text-white mb-2">Join to Enter Scores</h3>
                      <p className="text-muted-foreground mb-6">
                        You need to join this tournament before you can enter scores.
                      </p>
                      <Button 
                        onClick={handleJoin}
                        className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-pill"
                        data-testid="join-to-score-btn"
                      >
                        Join Tournament
                      </Button>
                    </>
                  ) : (
                    <>
                      <Lock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                      <h3 className="text-xl font-heading text-white mb-2">Pro Members Only</h3>
                      <p className="text-muted-foreground mb-6">
                        This is a Pro Tour tournament. Upgrade your membership to participate{tournament?.allow_paid_entry ? ' — or pay a one-time $10 entry.' : '.'}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        {tournament?.allow_paid_entry && (
                          <Button
                            onClick={handlePayEntry}
                            disabled={payingEntry}
                            className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-pill"
                            data-testid="pay-entry-btn-panel"
                          >
                            {payingEntry ? 'Redirecting…' : 'Pay $10 to Enter'}
                          </Button>
                        )}
                        <Button
                          onClick={() => navigate('/profile')}
                          className="bg-gradient-to-r from-amber-500 to-orange-500 text-black font-semibold rounded-pill"
                          data-testid="upgrade-to-join-btn"
                        >
                          <Crown className="mr-2 h-4 w-4" />
                          Upgrade to Pro
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Leaderboard Tab — Live via WebSocket */}
          <TabsContent value="leaderboard">
            <Card className="bg-card border-white/10 overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-primary" />
                  {isTeamTournament ? 'Team Leaderboard' : 'Leaderboard'}
                </CardTitle>
                <div className="flex items-center gap-2" data-testid="ws-status">
                  {wsConnected ? (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-500/15 text-green-400 border border-green-500/30">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                      LIVE
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-zinc-500/15 text-zinc-400 border border-zinc-500/30">
                      <WifiOff className="w-3 h-3" />
                      Offline
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {isTeamTournament ? (
                  /* Team Leaderboard */
                  teamLeaderboard.length === 0 ? (
                    <div className="p-12 text-center">
                      <Users2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-xl font-heading text-white mb-2">No Teams Yet</h3>
                      <p className="text-muted-foreground">Create a team and start scoring!</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <div className="grid grid-cols-[50px_1fr_60px_60px_60px] sm:grid-cols-[60px_1fr_80px_80px_80px] items-center px-4 py-3 border-b border-white/10 font-heading text-sm text-muted-foreground">
                        <div>Rank</div>
                        <div>Team</div>
                        <div className="text-center">Thru</div>
                        <div className="text-right">Total</div>
                        <div className="text-right">To Par</div>
                      </div>
                      {teamLeaderboard.map((entry, index) => {
                        const thruLabel = getThruLabel(entry.holes_completed, tournament.num_holes);
                        const isMyTeam = myTeam && entry.team_id === myTeam.id;
                        return (
                          <div
                            key={entry.team_id}
                            className={`grid grid-cols-[50px_1fr_60px_60px_60px] sm:grid-cols-[60px_1fr_80px_80px_80px] items-center px-4 py-3 border-b border-white/5 transition-colors ${
                              isMyTeam ? 'bg-primary/10' : 'hover:bg-white/[0.02]'
                            }`}
                            data-testid={`team-leaderboard-row-${index}`}
                          >
                            <div>{getRankBadge(entry.rank)}</div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <Users2 className="w-3.5 h-3.5 text-secondary shrink-0" />
                                <span className="text-white font-medium truncate">{entry.team_name}</span>
                              </div>
                              <div className="text-muted-foreground text-xs truncate">{entry.captain_name} & {entry.partner_name}</div>
                            </div>
                            <div className="text-center">
                              <span className={`font-mono text-sm ${thruLabel === 'F' ? 'text-secondary font-bold' : 'text-muted-foreground'}`}>
                                {thruLabel === 'F' ? 'F' : thruLabel === '-' ? '-' : thruLabel}
                              </span>
                            </div>
                            <div className="text-right font-mono text-lg text-white">
                              {entry.total_strokes || '-'}
                            </div>
                            <div className={`text-right font-mono text-lg font-bold ${
                              entry.score_to_par < 0 ? 'text-secondary text-glow-green' :
                              entry.score_to_par === 0 ? 'text-primary' : 'text-destructive'
                            }`}>
                              {entry.holes_completed > 0 ? entry.score_label : '-'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                ) : (
                  /* Individual Leaderboard */
                  leaderboard.length === 0 ? (
                    <div className="p-12 text-center">
                      <Medal className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-xl font-heading text-white mb-2">No Scores Yet</h3>
                      <p className="text-muted-foreground">Be the first to submit your scores!</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <div className="grid grid-cols-[60px_1fr_70px_70px_70px] sm:grid-cols-[60px_1fr_80px_80px_80px] items-center px-4 py-3 border-b border-white/10 font-heading text-sm text-muted-foreground">
                        <div>Rank</div>
                        <div>Player</div>
                        <div className="text-center">Thru</div>
                        <div className="text-right">Total</div>
                        <div className="text-right">To Par</div>
                      </div>
                      {leaderboard.map((entry, index) => {
                        const thruLabel = getThruLabel(entry.holes_completed, tournament.num_holes);
                        return (
                          <div 
                            key={entry.member_id}
                            className={`grid grid-cols-[60px_1fr_70px_70px_70px] sm:grid-cols-[60px_1fr_80px_80px_80px] items-center px-4 py-3 border-b border-white/5 transition-colors ${
                              entry.member_id === member?.id ? 'bg-primary/10' : 'hover:bg-white/[0.02]'
                            }`}
                            data-testid={`leaderboard-row-${index}`}
                          >
                            <div>{getRankBadge(entry.rank)}</div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-white font-medium truncate">{entry.member_name}</span>
                                {entry.badges?.some(b => b.type === 'gold_trophy') && (
                                  <span className="shrink-0 w-4 h-4 rounded-full bg-yellow-500/20 flex items-center justify-center" title="1st Place Champion">
                                    <Trophy className="w-2.5 h-2.5 text-yellow-300" />
                                  </span>
                                )}
                                {entry.badges?.some(b => b.type === 'silver_trophy') && (
                                  <span className="shrink-0 w-4 h-4 rounded-full bg-zinc-300/20 flex items-center justify-center" title="Silver Trophy">
                                    <Trophy className="w-2.5 h-2.5 text-zinc-200" />
                                  </span>
                                )}
                                {entry.badges?.some(b => b.type === 'bronze_trophy') && (
                                  <span className="shrink-0 w-4 h-4 rounded-full bg-amber-700/20 flex items-center justify-center" title="Bronze Trophy">
                                    <Trophy className="w-2.5 h-2.5 text-amber-500" />
                                  </span>
                                )}
                                {entry.badges?.some(b => b.type === 'recruiter') && (
                                  <span className="shrink-0 w-4 h-4 rounded-full bg-purple-500/20 flex items-center justify-center" title="Recruiter">
                                    <UserPlus className="w-2.5 h-2.5 text-purple-400" />
                                  </span>
                                )}
                              </div>
                              <div className="text-muted-foreground text-xs">{entry.member_number}</div>
                            </div>
                            <div className="text-center" data-testid={`thru-${entry.member_id}`}>
                              <span className={`font-mono text-sm ${thruLabel === 'F' ? 'text-secondary font-bold' : 'text-muted-foreground'}`}>
                                {thruLabel === 'F' ? 'F' : thruLabel === '-' ? '-' : `${thruLabel}`}
                              </span>
                            </div>
                            <div className="text-right font-mono text-lg text-white">
                              {entry.total_strokes || '-'}
                            </div>
                            <div className={`text-right font-mono text-lg font-bold ${
                              entry.score_to_par < 0 ? 'text-secondary text-glow-green' :
                              entry.score_to_par === 0 ? 'text-primary' : 'text-destructive'
                            }`}>
                              {entry.holes_completed > 0 ? entry.score_label : '-'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Sponsor Banner */}
        <SponsorBanner className="mt-12 border-t border-white/10 pt-8" />
      </main>
    </div>
  );
};
